import React from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export type SegmentedToggleOption = {
  label: string
  value: string
  /** Optional native tooltip for the segment. */
  title?: string
}

/**
 * `pill`   — rounded container with a pink active segment (default look).
 * `joined` — daisyUI `join` bar with a blue active segment (used by date
 *            granularity toggles).
 */
export type SegmentedToggleVariant = "pill" | "joined"

type SegmentedToggleProps = {
  id?: string
  options: SegmentedToggleOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  variant?: SegmentedToggleVariant
  className?: string
}

const CONTAINER_CLASS: Record<SegmentedToggleVariant, string> = {
  pill: "inline-flex rounded-lg bg-basic-dark-blue border border-basic-blue p-1",
  joined: "join h-full",
}

const segmentClass = (variant: SegmentedToggleVariant, active: boolean) =>
  variant === "joined"
    ? cn(
        "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md text-sm font-medium text-white transition-colors",
        active
          ? "bg-system-blue-600"
          : "bg-system-blue-800 hover:bg-system-blue-700",
      )
    : cn(
        "px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
        active ? "bg-pink text-white" : "text-gray-400 hover:text-white",
      )

const SegmentedToggle: React.FC<SegmentedToggleProps> = ({
  id,
  options,
  value,
  onChange,
  disabled = false,
  variant = "pill",
  className,
}) => (
  <div
    id={id}
    className={cn(
      CONTAINER_CLASS[variant],
      disabled && "opacity-50 pointer-events-none",
      className,
    )}
  >
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        title={option.title}
        onClick={() => onChange(option.value)}
        disabled={disabled}
        className={segmentClass(variant, value === option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
)

export default SegmentedToggle
